import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useT } from "@/i18n";
import { Starfield } from "@/components/Starfield";
import { Sparkles, Globe2, BookOpen, Feather } from "lucide-react";

const MAP_IMG =
  "https://static.prod-images.emergentagent.com/jobs/6ecf66ec-c1dc-455e-81ff-a03b57beae15/images/e9d3e491940620c2952e37d4e2652450b6d3bed5365af7fb2a3c85b45049b1f8.png";

export default function Login() {
  const navigate = useNavigate();
  const { t } = useT();

  const enter = () => navigate("/chat");

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const token = localStorage.getItem("session_token");
    if (token) localStorage.setItem("link_guest_token", token);
    const redirectUrl = window.location.origin + "/chat";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    { icon: BookOpen, title: t("feat.diary.t"), desc: t("feat.diary.d") },
    { icon: Feather, title: t("feat.ai.t"), desc: t("feat.ai.d") },
    { icon: Globe2, title: t("feat.map.t"), desc: t("feat.map.d") },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#050814] text-[#FDFBF7]">
      <img
        src={MAP_IMG}
        alt="world"
        className="absolute inset-0 h-full w-full object-cover opacity-30"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050814]/60 via-[#050814]/80 to-[#050814]" />
      <Starfield />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col items-center justify-center px-6 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-black/30 px-4 py-1.5 text-xs tracking-[0.2em] text-[#D4AF37] backdrop-blur-md">
            <Sparkles className="h-3.5 w-3.5" /> {t("app.badge")}
          </div>
          <h1 className="font-decorative text-4xl leading-tight tracking-tight sm:text-6xl">
            <span className="gold-text">{t("app.name")}</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl font-sans text-base leading-relaxed text-[#A3B1C6] sm:text-lg">
            {t("login.desc")}
          </p>

          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.98 }}
            onClick={enter}
            data-testid="enter-app-button"
            className="crystal mt-10 inline-flex items-center gap-3 bg-gradient-to-r from-[#D4AF37] to-[#f0d787] px-8 py-4 font-medium text-[#050814] shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-shadow hover:shadow-[0_0_50px_rgba(212,175,55,0.6)]"
          >
            <Sparkles className="h-5 w-5" />
            {t("login.enter")}
          </motion.button>
          <div className="mt-4">
            <button
              onClick={googleLogin}
              data-testid="google-login-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-2.5 text-sm text-[#FDFBF7] backdrop-blur-md transition-colors hover:border-[#D4AF37]/50 hover:bg-white/10"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
              {t("login.google")}
            </button>
          </div>
          <p className="mt-4 text-xs text-[#A3B1C6]/70">{t("login.subnote")}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="mt-16 grid w-full gap-4 sm:grid-cols-3"
        >
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-2xl border border-[#D4AF37]/15 bg-black/30 p-6 text-left backdrop-blur-md"
            >
              <f.icon className="mb-3 h-6 w-6 text-[#D4AF37]" />
              <h3 className="font-serif text-xl text-[#FDFBF7]">{f.title}</h3>
              <p className="mt-1 text-sm text-[#A3B1C6]">{f.desc}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}
